import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Pencil, Send, ShieldCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { SUPPLEMENTARY_FORM_CODES_SQL } from '../lib/formCodes';
import LifecycleTimeline from './LifecycleTimeline';
import { useMatterTimeline } from '../lib/useMatterTimeline';
import {
  STATUS_AWAITING_FINAL_REVIEW,
  STATUS_APPROVED,
  STATUS_REFUSED,
  STATUS_WITHDRAWN,
  isArchivedStatus,
} from '../lib/matterStatus';
import {
  getDeFactoEvidence,
  deFactoPathLabel,
  resolveDeFactoIntakeFields,
  type DeFactoBasisPath,
  type DeFactoEvidence,
  type DeFactoRecordedBasis,
} from '../lib/deFactoEligibility';
import {
  canLodge as engagementLetterCanLodge,
  type EngagementLetterState,
} from '../lib/engagementLetter';
import { expandFormState, rowsToFormState } from '../lib/schema-runtime/formStateAdapter';

interface Props {
  matterId: string;
  visaSubclass: string;
  variant: string | null;
  applicantContactId: string;
  sponsorContactId: string | null;
  hasMinorDependents?: boolean;
  matterStatus: string;
  /** Full matter context so the timeline can compute payment-step done-ness
      consistently with the client view. Without these, the staff timeline
      thinks payments were never received. */
  initialPaymentDate?: string | null;
  finalPaymentDate?: string | null;
  reloadKey?: number;
}

const PRE_FINAL_STATUSES = new Set(['In Progress', 'Awaiting Review']);

/**
 * Statuses where lodgement basis must be recorded for defacto matters. The
 * DB trigger fn_check_defacto_eligibility_basis() is authoritative; this UI
 * set mirrors it so the gate can suppress CTAs cleanly before the trigger
 * fires. Keep these two lists in sync.
 */
const POST_REVIEW_STATUSES = new Set<string>([
  'Ready for lodgement',
  'Lodged with Home Affairs',
  'Approved',
  'Refused',
  'Withdrawn',
]);

/**
 * Staff readiness panel. Renders the unified context-driven timeline plus the
 * "Send to Eli for final review" CTA when all gates are green and the matter
 * is in a pre-final-review state.
 *
 * For variant=defacto matters: also renders the evidence-presented + Eli
 * sign-off panel. Eli ticks one of three statutory paths (Migration Act
 * s5CB), writes a basis note, and saves to matters.de_facto_eligibility_basis.
 * Without that basis recorded, the matter can't transition into
 * Ready-for-lodgement / Lodged / Approved / Refused / Withdrawn — both the UI
 * suppresses the CTAs and the DB trigger rejects the write.
 */
export default function LodgementGate({
  matterId,
  visaSubclass,
  variant,
  applicantContactId,
  sponsorContactId,
  hasMinorDependents = false,
  matterStatus,
  initialPaymentDate = null,
  finalPaymentDate = null,
  reloadKey = 0,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bumpKey, setBumpKey] = useState(0);
  const [currentStatus, setCurrentStatus] = useState<string>(matterStatus);
  const [evidence, setEvidence] = useState<DeFactoEvidence | null>(null);
  const [evidenceChecked, setEvidenceChecked] = useState(false);
  const [editingBasis, setEditingBasis] = useState(false);
  const [engagementLetterOk, setEngagementLetterOk] = useState(true);
  // Admin toggle (app_settings.engagement_letter_gate_enabled). When false the
  // portal still tracks engagement letters but does not block lodgement.
  const [engagementGateEnabled, setEngagementGateEnabled] = useState(true);

  const { steps, ctx } = useMatterTimeline({
    matterId,
    visaSubclass,
    variant,
    applicantContactId,
    sponsorContactId,
    hasMinorDependents,
    matter: { status: currentStatus, initial_payment_date: initialPaymentDate, final_payment_date: finalPaymentDate },
    reloadKey: reloadKey + bumpKey,
  });

  // Gates met when documents are all approved AND intakes are completed.
  // Derived from the timeline steps so it can never disagree with what's
  // shown to the user.
  const gatesMet = steps.length > 0 && steps.every((s) =>
    s.id === 'cm_review' || s.id === 'final_review' || s.id === 'ready_for_lodgement' || s.id === 'final_invoice' || s.id === 'lodged' || s.id === 'approved'
      ? true
      : s.status === 'done'
  ) && (ctx?.applicantIntakeStatus === 'completed' || ctx?.applicantIntakeStatus === 'submitted')
    && (!ctx?.sponsorIntakeBlocksLodgement || ctx?.sponsorIntakeStatus === 'completed' || ctx?.sponsorIntakeStatus === 'submitted')
    && ctx?.requiredDocsTotal !== undefined
    && ctx.requiredDocsTotal > 0
    && ctx.requiredDocsApproved === ctx.requiredDocsTotal;

  // Re-evaluate the de-facto evidence whenever the matter context changes.
  // Pulls applicant intake fields, the relationship_certificate doc, the
  // approved relationship-evidence count, and the recorded basis itself.
  useEffect(() => {
    let cancelled = false;
    async function loadEvidence() {
      if (variant !== 'defacto') {
        setEvidence(null);
        setEvidenceChecked(true);
        return;
      }
      try {
        // Recorded basis on the matter.
        const { data: matter } = await supabase
          .from('matters')
          .select('de_facto_eligibility_basis')
          .eq('id', matterId)
          .maybeSingle();
        const recordedBasis = ((matter as { de_facto_eligibility_basis: DeFactoRecordedBasis | null } | null)?.de_facto_eligibility_basis) ?? null;

        // Applicant intake — claimed dates + narrative. intake_field_data
        // stores one row per top-level canonical segment holding the whole
        // nested object (autosave), plus occasional flat dotted seed rows
        // (agent prefill) — a literal dotted field_key lookup never matches
        // that blob shape. Fetch every row for the form and hydrate via
        // expandFormState(rowsToFormState(...)), the same path the intake
        // form itself uses to reconstruct nested state.
        const { data: intakes } = await supabase
          .from('intake_forms')
          .select('id')
          .eq('matter_id', matterId)
          .eq('contact_id', applicantContactId)
          .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
          .order('last_saved_at', { ascending: false })
          .limit(1);
        const intakeId = (intakes ?? [])[0]?.id as string | undefined;

        let applicantRows: Array<{ field_key: string; field_value: unknown }> = [];
        if (intakeId) {
          const { data: rows } = await supabase
            .from('intake_field_data')
            .select('field_key, field_value')
            .eq('intake_form_id', intakeId);
          applicantRows = (rows ?? []) as Array<{ field_key: string; field_value: unknown }>;
        }

        // Sponsor intake — the sponsor's 40SP form also renders these SAME
        // applicant-owned fields in its stage 1 (confirming shared
        // relationship facts). Early in a matter, before the applicant has
        // reached their own relationship section, only the sponsor-side copy
        // exists (see B5 evidence). No sponsor contact / no sponsor form yet
        // is a normal state here, not an error — handled gracefully below.
        let sponsorRows: Array<{ field_key: string; field_value: unknown }> | null = null;
        if (sponsorContactId) {
          const { data: sponsorIntakes } = await supabase
            .from('intake_forms')
            .select('id')
            .eq('matter_id', matterId)
            .eq('contact_id', sponsorContactId)
            .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
            .order('last_saved_at', { ascending: false })
            .limit(1);
          const sponsorIntakeId = (sponsorIntakes ?? [])[0]?.id as string | undefined;
          if (sponsorIntakeId) {
            const { data: rows } = await supabase
              .from('intake_field_data')
              .select('field_key, field_value')
              .eq('intake_form_id', sponsorIntakeId);
            sponsorRows = (rows ?? []) as Array<{ field_key: string; field_value: unknown }>;
          }
        }

        const applicantState = expandFormState(rowsToFormState(applicantRows));
        const sponsorState = sponsorRows ? expandFormState(rowsToFormState(sponsorRows)) : null;
        // Precedence: the applicant's own value wins; the sponsor-form value
        // fills the gap only when the applicant's is empty/undefined.
        const { dateDeFactoBegan, compellingCircumstances: compelling } = resolveDeFactoIntakeFields(
          applicantState,
          sponsorState,
        );

        // Registered relationship cert (existence + first doc id for preview).
        const { data: certDocs } = await supabase
          .from('documents')
          .select('id')
          .eq('matter_id', matterId)
          .eq('checklist_item', 'relationship_certificate')
          .is('deleted_at', null)
          .order('uploaded_at', { ascending: false })
          .limit(1);
        const cert = (certDocs ?? [])[0] as { id: string } | undefined;

        // Approved cohabitation evidence docs.
        const { count: cohabCount } = await supabase
          .from('documents')
          .select('id', { count: 'exact', head: true })
          .eq('matter_id', matterId)
          .eq('checklist_item', 'relationship_evidence')
          .eq('review_status', 'approved')
          .is('deleted_at', null);

        const next = getDeFactoEvidence({
          variant,
          dateDeFactoBegan,
          hasRegisteredRelationshipCert: !!cert,
          registeredCertDocId: cert?.id ?? null,
          compellingCircumstances: compelling,
          cohabitationEvidenceDocsApproved: cohabCount ?? 0,
          recordedBasis,
        });

        if (cancelled) return;
        setEvidence(next);
        setEvidenceChecked(true);
      } catch (err) {
        if (cancelled) return;
        console.warn('de-facto evidence load failed', err);
        setEvidence(null);
        setEvidenceChecked(true);
      }
    }
    void loadEvidence();
    return () => {
      cancelled = true;
    };
  }, [variant, matterId, applicantContactId, sponsorContactId, reloadKey, bumpKey]);

  // Engagement-letter gate (applies to ALL matters, not just defacto). Mirrors
  // fn_check_engagement_letter_before_lodgement in the DB. Re-fetches on the
  // shared reloadKey so a just-recorded letter opens the gate without a reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('matters')
        .select('engagement_letter')
        .eq('id', matterId)
        .maybeSingle();
      if (cancelled) return;
      const el = (data as { engagement_letter: EngagementLetterState | null } | null)?.engagement_letter ?? null;
      setEngagementLetterOk(engagementLetterCanLodge(el));

      // Admin toggle: gate is enabled unless explicitly set false.
      const { data: flag } = await supabase
        .from('app_settings' as never)
        .select('value')
        .eq('key', 'engagement_letter_gate_enabled')
        .maybeSingle();
      if (cancelled) return;
      const v = (flag as { value: unknown } | null)?.value;
      setEngagementGateEnabled(v !== false);
    })();
    return () => {
      cancelled = true;
    };
  }, [matterId, reloadKey, bumpKey]);

  /** True when the engagement letter must be on file before lodgement. Only
      enforced when the admin gate toggle is on. */
  const engagementLetterRequired = engagementGateEnabled && !engagementLetterOk
    && (currentStatus === STATUS_AWAITING_FINAL_REVIEW || POST_REVIEW_STATUSES.has(currentStatus));

  /** True when the defacto basis must be recorded before further transition. */
  const basisRequired = !!evidence?.applies && !evidence.basisRecorded
    && (currentStatus === STATUS_AWAITING_FINAL_REVIEW || POST_REVIEW_STATUSES.has(currentStatus));

  // CTA active only when all gates are met, status is pre-final-review, AND
  // (if defacto) the basis is recorded (or matter is not yet at the gating
  // status — pre-final-review is below the trigger threshold so the trigger
  // would allow the Awaiting-Eli transition itself; but we still want Eli to
  // record the basis before sending it on, since the next step is lodgement).
  const ctaActive = gatesMet && PRE_FINAL_STATUSES.has(currentStatus)
    && (!evidence?.applies || evidence.basisRecorded);

  const transitionStatus = async (next: string) => {
    setError(null);
    setSubmitting(true);
    try {
      const { error: err } = await supabase.from('matters').update({ status: next }).eq('id', matterId);
      if (err) throw err;
      setCurrentStatus(next);
      setBumpKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update status.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendToFinalReview = async () => {
    setError(null);
    setSubmitting(true);
    try {
      // Find the reviewer (admin first, then a contact whose given_name starts "eli").
      let { data: reviewer } = await supabase
        .from('contacts')
        .select('id, email')
        .eq('contact_type', 'staff')
        .eq('staff_role', 'admin')
        .is('deleted_at', null)
        .limit(1)
        .maybeSingle();
      if (!reviewer) {
        const { data: byName } = await supabase
          .from('contacts')
          .select('id, email')
          .eq('contact_type', 'staff')
          .ilike('given_names', 'eli%')
          .is('deleted_at', null)
          .limit(1)
          .maybeSingle();
        reviewer = byName as { id: string; email: string | null } | null;
      }
      if (!reviewer) {
        throw new Error('No reviewer (admin / Eli) found in staff. Set staff_role=admin on a contact and retry.');
      }
      const patch = {
        status: STATUS_AWAITING_FINAL_REVIEW,
        reviewer_staff_id: (reviewer as { id: string }).id,
      } as unknown;
      const { error: updErr } = await supabase
        .from('matters')
        .update(patch as never)
        .eq('id', matterId);
      if (updErr) throw updErr;
      setCurrentStatus(STATUS_AWAITING_FINAL_REVIEW);
      setBumpKey((k) => k + 1);

      // Best-effort email notification to the reviewer.
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;
        const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) ?? '';
        if (accessToken && supabaseUrl) {
          await fetch(`${supabaseUrl}/functions/v1/send-final-review-notice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify({ matter_id: matterId }),
          });
        }
      } catch (mailErr) {
        console.warn('final-review email failed', mailErr);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send to Eli.');
    } finally {
      setSubmitting(false);
    }
  };

  const archived = isArchivedStatus(currentStatus);

  // Suppress timeline transitions when a hard gate is unmet (de-facto basis or
  // engagement letter). The DB triggers reject the write anyway, but suppressing
  // here gives a clean UX and a clear reason.
  const timelineOnChange = (basisRequired || engagementLetterRequired) ? undefined : transitionStatus;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4 text-sm">
      {/* De-facto eligibility evidence + sign-off — only on variant=defacto. */}
      {evidenceChecked && evidence?.applies && (
        <DeFactoBasisPanel
          matterId={matterId}
          evidence={evidence}
          basisRequired={basisRequired}
          editing={editingBasis}
          setEditing={setEditingBasis}
          onSaved={() => {
            setEditingBasis(false);
            setBumpKey((k) => k + 1);
          }}
        />
      )}

      {ctaActive && (
        <button
          type="button"
          onClick={handleSendToFinalReview}
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Send to Eli for final review
        </button>
      )}

      {/* Explain why the CTA is hidden when the only blocker is the missing
          basis on a defacto matter that's otherwise gates-met. */}
      {gatesMet && PRE_FINAL_STATUSES.has(currentStatus) && evidence?.applies && !evidence.basisRecorded && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 text-xs flex items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div>Record the de-facto eligibility basis above before sending this matter to Eli for final review.</div>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
      )}

      {/* Engagement-letter gate explainer — shown when it's the blocker. */}
      {engagementLetterRequired && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 text-xs flex items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div>
            Engagement letter not on file. Upload the signed engagement letter PDF (Engagement letter
            card above) before this matter can be lodged. MARA Code of Conduct reg. 6 + 14.
          </div>
        </div>
      )}

      {/* Make it visible when the gate has been switched off, so it's never a
          silent trapdoor. */}
      {!engagementGateEnabled && !engagementLetterOk && (
        <div className="text-[11px] text-slate-400">
          Engagement-letter lodgement gate is currently OFF (admin setting); lodgement is not blocked.
        </div>
      )}

      <LifecycleTimeline
        steps={steps}
        mode="staff"
        onChange={timelineOnChange}
        showTerminalActions={!archived && !PRE_FINAL_STATUSES.has(currentStatus) && !basisRequired && !engagementLetterRequired}
        onTerminal={transitionStatus}
      />

      {archived && (
        <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${
          currentStatus === STATUS_APPROVED ? 'border-emerald-200 bg-emerald-50 text-emerald-900' :
          currentStatus === STATUS_REFUSED ? 'border-red-200 bg-red-50 text-red-900' :
          'border-slate-200 bg-slate-50 text-slate-900'
        }`}>
          <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">{currentStatus}</div>
            <div className="opacity-80">
              {currentStatus === STATUS_APPROVED && 'Visa granted. Matter has moved to Approved matters in the sidebar.'}
              {currentStatus === STATUS_REFUSED && 'Refused. Matter has moved to Approved matters in the sidebar.'}
              {currentStatus === STATUS_WITHDRAWN && 'Withdrawn. Matter has moved to Approved matters in the sidebar.'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// De-facto eligibility basis panel
// ===========================================================================

interface BasisPanelProps {
  matterId: string;
  evidence: DeFactoEvidence;
  basisRequired: boolean;
  editing: boolean;
  setEditing: (v: boolean) => void;
  onSaved: () => void;
}

const STATE_REGISTRIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Vic RBDM', label: 'Victoria - RBDM' },
  { value: 'NSW', label: 'New South Wales' },
  { value: 'Qld', label: 'Queensland' },
  { value: 'Tas', label: 'Tasmania' },
  { value: 'ACT', label: 'ACT' },
  { value: 'Other', label: 'Other / overseas' },
];

function DeFactoBasisPanel({
  matterId,
  evidence,
  basisRequired,
  editing,
  setEditing,
  onSaved,
}: BasisPanelProps) {
  // Recorded view (green banner + edit button).
  if (evidence.basisRecorded && evidence.recordedBasis && !editing) {
    return (
      <RecordedBasisBanner basis={evidence.recordedBasis} onEdit={() => setEditing(true)} />
    );
  }

  // Form (editing OR no basis recorded yet).
  return (
    <BasisForm
      matterId={matterId}
      evidence={evidence}
      basisRequired={basisRequired}
      onCancel={evidence.basisRecorded ? () => setEditing(false) : undefined}
      onSaved={onSaved}
    />
  );
}

function RecordedBasisBanner({
  basis,
  onEdit,
}: {
  basis: DeFactoRecordedBasis;
  onEdit: () => void;
}) {
  const [staffName, setStaffName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data } = await supabase
        .from('contacts')
        .select('given_names, family_name')
        .eq('id', basis.decided_by_staff_id)
        .maybeSingle();
      if (cancelled) return;
      const c = data as { given_names: string | null; family_name: string | null } | null;
      if (c) {
        setStaffName(`${c.given_names ?? ''} ${c.family_name ?? ''}`.trim() || null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [basis.decided_by_staff_id]);

  const decidedAt = useMemo(() => {
    try {
      return new Date(basis.decided_at).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return basis.decided_at;
    }
  }, [basis.decided_at]);

  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-emerald-900 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <ShieldCheck size={16} className="shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="font-semibold">
              Eligibility: {deFactoPathLabel(basis.path)}
            </div>
            <div className="text-emerald-800 text-xs">
              Recorded by {staffName ?? 'staff'} on {decidedAt}.
            </div>
            <div className="text-emerald-900 text-sm">
              <span className="font-semibold">Basis:</span> {basis.note}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-emerald-900 hover:bg-emerald-100"
        >
          <Pencil size={12} /> Edit
        </button>
      </div>
    </div>
  );
}

function BasisForm({
  matterId,
  evidence,
  basisRequired,
  onCancel,
  onSaved,
}: {
  matterId: string;
  evidence: DeFactoEvidence;
  basisRequired: boolean;
  onCancel?: () => void;
  onSaved: () => void;
}) {
  const [path, setPath] = useState<DeFactoBasisPath | null>(
    evidence.recordedBasis?.path ?? null,
  );
  const [registry, setRegistry] = useState<string>('Vic RBDM');
  const [note, setNote] = useState<string>(evidence.recordedBasis?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSave = path !== null && note.trim().length >= 10 && !saving;

  const handleSave = async () => {
    if (!path) {
      setErr('Tick exactly one statutory path.');
      return;
    }
    if (note.trim().length < 10) {
      setErr('Basis note must be at least 10 characters.');
      return;
    }
    setErr(null);
    setSaving(true);
    try {
      // Resolve current staff contact id from auth.uid → contacts.user_id.
      const { data: userData } = await supabase.auth.getUser();
      const authUserId = userData.user?.id;
      if (!authUserId) {
        throw new Error('Not signed in.');
      }
      const { data: staff } = await supabase
        .from('contacts')
        .select('id')
        .eq('user_id', authUserId)
        .eq('contact_type', 'staff')
        .is('deleted_at', null)
        .maybeSingle();
      const decidedByStaffId = (staff as { id: string } | null)?.id;
      if (!decidedByStaffId) {
        throw new Error('Your auth user is not linked to a staff contact. Ask an admin.');
      }

      const finalNote = path === 'state_registered'
        ? `${note.trim()} (registry: ${registry})`
        : note.trim();

      const basis: DeFactoRecordedBasis = {
        path,
        note: finalNote,
        decided_by_staff_id: decidedByStaffId,
        decided_at: new Date().toISOString(),
      };

      const updatePayload = { de_facto_eligibility_basis: basis } as unknown;
      const { error: updErr } = await supabase
        .from('matters')
        .update(updatePayload as never)
        .eq('id', matterId);
      if (updErr) throw updErr;

      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save basis.');
    } finally {
      setSaving(false);
    }
  };

  const headerTone = basisRequired ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50';
  const headerText = basisRequired ? 'text-amber-900' : 'text-slate-900';

  return (
    <div className={`rounded-md border ${headerTone} p-3 ${headerText} space-y-3 text-sm`}>
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className={`shrink-0 mt-0.5 ${basisRequired ? 'text-amber-700' : 'text-slate-500'}`} />
        <div className="space-y-1">
          <div className="font-semibold">De-facto eligibility evidence</div>
          <div className="text-xs opacity-90">
            The portal can't decide eligibility. Eli (registered migration agent, MARN 1465541) ticks one of three statutory paths and records a basis note. Required before lodgement under Migration Act s5CB / Migration Agents Code of Conduct reg. 8.
          </div>
        </div>
      </div>

      {/* Path 1 — 12 months cohabitation */}
      <PathRow
        checked={path === 'cohabitation_12_months'}
        onCheck={() => setPath('cohabitation_12_months')}
        title="12 months cohabitation"
        verifyLabel="Verified: 12 months cohabitation confirmed"
      >
        <div className="text-xs opacity-90 space-y-0.5">
          <div>
            Claimed start date:{' '}
            <span className="font-mono">{evidence.evidence.cohabitation.startDate ?? '-'}</span>
          </div>
          <div>
            Claimed months elapsed:{' '}
            <span className="font-mono">
              {evidence.evidence.cohabitation.monthsElapsed !== null
                ? evidence.evidence.cohabitation.monthsElapsed.toFixed(1)
                : '-'}
            </span>
          </div>
          <div>
            Cohabitation evidence docs uploaded:{' '}
            <span className="font-mono">{evidence.evidence.cohabitation.evidenceDocsApproved}</span>
          </div>
        </div>
      </PathRow>

      {/* Path 2 — State-registered */}
      <PathRow
        checked={path === 'state_registered'}
        onCheck={() => setPath('state_registered')}
        title="State-registered relationship"
        verifyLabel="Verified: registered relationship certificate confirmed"
      >
        <div className="text-xs opacity-90 space-y-1">
          <div>
            Certificate uploaded:{' '}
            <span className="font-mono">
              {evidence.evidence.registered.certificateUploaded ? 'Yes' : 'No'}
            </span>
            {evidence.evidence.registered.certificateDocId && (
              <>
                {' '}
                <a
                  href={`/documents?view=${evidence.evidence.registered.certificateDocId}`}
                  className="underline hover:no-underline ml-2"
                >
                  Preview
                </a>
              </>
            )}
          </div>
          {path === 'state_registered' && (
            <label className="block">
              <span className="block mb-1">State registry</span>
              <select
                value={registry}
                onChange={(e) => setRegistry(e.target.value)}
                className="block w-full max-w-xs rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900"
              >
                {STATE_REGISTRIES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </PathRow>

      {/* Path 3 — Compelling and compassionate */}
      <PathRow
        checked={path === 'compelling_circumstances'}
        onCheck={() => setPath('compelling_circumstances')}
        title="Compelling and compassionate circumstances"
        verifyLabel="Verified: circumstances accepted"
      >
        <div className="text-xs opacity-90">
          <div className="mb-1">Narrative from intake (verbatim):</div>
          <div className="rounded bg-white border border-slate-200 p-2 whitespace-pre-wrap font-mono text-[11px] max-h-40 overflow-y-auto text-slate-800">
            {evidence.evidence.compelling.narrative ?? '(none entered)'}
          </div>
        </div>
      </PathRow>

      <div>
        <label className="block">
          <span className="block text-xs font-semibold mb-1">Basis note</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="e.g. 'Couple has Vic RBDM cert dated 2023-05-12, registry verified'"
            className="block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
          />
        </label>
        <div className="text-[11px] opacity-70 mt-1">
          Minimum 10 characters. Describes the evidence Eli relied on. Captured in audit log on save.
        </div>
      </div>

      {err && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">{err}</div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
          Record eligibility basis
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 rounded border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function PathRow({
  checked,
  onCheck,
  title,
  verifyLabel,
  children,
}: {
  checked: boolean;
  onCheck: () => void;
  title: string;
  verifyLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`rounded border p-2 ${checked ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}>
      <div className="font-semibold text-sm mb-1">{title}</div>
      <div className="mb-2">{children}</div>
      <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
        <input
          type="checkbox"
          checked={checked}
          onChange={onCheck}
          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        <span>{verifyLabel}</span>
      </label>
    </div>
  );
}
